import { Fragment, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, adminAuth, authQuery } from "@/lib/api";
import { TopNav, Footer } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { LogOut, Loader2, Search, MoreVertical, Users, FileText, ClipboardCheck, Send, Download, Mail, ChevronDown, ChevronUp, Zap, Hand, KeyRound, Leaf, Inbox, GraduationCap, ArrowRightLeft, X, UserX, UserPlus, Bell, MessageSquare, CheckCircle2 } from "lucide-react";
import { editionFor, PRODUCT_NAME } from "@/lib/kcisEdition";
import PractitionerActions from "@/components/PractitionerActions";
import { toast } from "sonner";

const StatusBadge = ({ status, label }) => {
  const map = {
    generated: "bg-emerald-50 text-emerald-700 border-emerald-200",
    pending: "bg-amber-50 text-amber-800 border-amber-200",
    error: "bg-red-50 text-red-700 border-red-200",
    submitted: "bg-purple-50 text-purple-900 border-purple-100",
    not_submitted: "bg-slate-100 text-slate-600 border-slate-200",
    sent: "bg-emerald-50 text-emerald-700 border-emerald-200",
    partial: "bg-amber-50 text-amber-800 border-amber-200",
    failed: "bg-red-50 text-red-700 border-red-200",
    skipped: "bg-amber-50 text-amber-800 border-amber-200",
    not_attempted: "bg-slate-100 text-slate-600 border-slate-200",
  };
  return <span className={`text-xs rounded-full px-3 py-1 border ${map[status] || map.pending}`}>{label}</span>;
};

const Stat = ({ icon: Icon, label, value }) => (
  <div className="kcis-card p-6">
    <div className="flex items-center justify-between">
      <div className="kcis-overline">{label}</div>
      <Icon className="h-4 w-4 text-purple-900" />
    </div>
    <div className="font-display text-3xl text-slate-900 mt-3">{value}</div>
  </div>
);

const AssessmentDrawer = ({ assessment }) => {
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [accessLogs, setAccessLogs] = useState([]);
  const [accessLoading, setAccessLoading] = useState(true);

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const { data } = await api.get(`/assessments/${assessment.id}/sent-history`);
      setHistory(data);
    } catch {
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const loadAccessLogs = async () => {
    setAccessLoading(true);
    try {
      const { data } = await api.get(`/clients/${assessment.client_id}/access-logs?limit=10`);
      setAccessLogs(data);
    } catch {
      setAccessLogs([]);
    } finally {
      setAccessLoading(false);
    }
  };

  useEffect(() => {
    loadHistory();
    loadAccessLogs();
    /* eslint-disable-next-line */
  }, [assessment.id]);

  const sendReport = async () => {
    if (assessment.report_status !== "generated") {
      toast.error("Generate the report first before sending.");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post(`/assessments/${assessment.id}/send-report`);
      if (data.status === "sent") {
        toast.success(`Report sent to ${assessment.email}`);
      } else if (data.status === "skipped") {
        toast.info("Email delivery not configured. PDF saved — use Download PDF.");
      }
      await loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  const downloadPdf = () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/assessments/${assessment.id}/report.pdf?download=1&${authQuery()}`;
    window.open(url, "_blank", "noopener");
    toast.success("PDF opened — your browser will save or display it");
  };

  return (
    <div className="bg-slate-50/60 border-t border-slate-100 p-6" data-testid={`admin-detail-${assessment.id}`}>
      {(assessment.auto_send_status === "failed" || assessment.auto_send_status === "partial") && (
        <div
          className="mb-5 rounded-xl border border-red-200 bg-red-50 p-4 flex items-start gap-3"
          data-testid={`admin-auto-send-failed-${assessment.id}`}
        >
          <div className="mt-0.5 h-2.5 w-2.5 rounded-full bg-red-500 shrink-0" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-red-800">
              {assessment.auto_send_status === "failed" ? "Auto-send failed" : "Auto-send partially failed"}
            </div>
            <p className="text-xs text-red-700 mt-1">
              {assessment.auto_send_status === "failed"
                ? "The report could not be auto-delivered to the client or practitioner. Use Send Report below as a manual backup."
                : "Either the client or practitioner copy did not deliver. Review the sent history below and retry if needed."}
            </p>
          </div>
        </div>
      )}
      <div className="flex flex-wrap gap-2 mb-5">
        <Button
          onClick={sendReport}
          disabled={sending || assessment.report_status !== "generated"}
          className="rounded-full bg-purple-900 hover:bg-purple-800 text-white"
          data-testid={`admin-send-report-${assessment.id}`}
        >
          {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
          Send Report to Client
        </Button>
        <Button
          onClick={downloadPdf}
          disabled={assessment.report_status !== "generated"}
          variant="outline"
          className="rounded-full border-slate-300"
          data-testid={`admin-download-pdf-${assessment.id}`}
        >
          <Download className="h-4 w-4 mr-2" />
          Download PDF
        </Button>
        <Link to={`/report/${assessment.id}`}>
          <Button variant="ghost" className="rounded-full text-slate-700">
            <FileText className="h-4 w-4 mr-2" /> View report
          </Button>
        </Link>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <span className="kcis-overline">Auto-send status:</span>
        <StatusBadge
          status={assessment.auto_send_status || "not_attempted"}
          label={
            assessment.auto_send_status === "sent" ? "Auto-sent" :
            assessment.auto_send_status === "partial" ? "Partial (client or practitioner failed)" :
            assessment.auto_send_status === "failed" ? "Auto-send failed" :
            assessment.auto_send_status === "skipped" ? "Skipped (no key)" : "Not attempted"
          }
        />
      </div>

      <div className="kcis-overline mb-3">Sent history</div>
      {historyLoading ? (
        <div className="text-sm text-slate-500 py-3"><Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading...</div>
      ) : history.length === 0 ? (
        <div className="text-sm text-slate-500 italic py-3">No sends yet.</div>
      ) : (
        <div className="space-y-2">
          {history.map((h) => (
            <div key={h.id} className="bg-white rounded-xl border border-slate-200 p-4 flex flex-wrap items-center justify-between gap-3 text-sm">
              <div className="flex items-center gap-3">
                <Mail className="h-4 w-4 text-purple-900" />
                <div>
                  <div className="text-slate-900 font-medium flex items-center gap-2">
                    {h.recipient_email}
                    <span className="text-[10px] uppercase tracking-wider text-slate-500 border border-slate-200 rounded px-1.5 py-0.5">{h.recipient_type || "client"}</span>
                  </div>
                  <div className="text-xs text-slate-500 flex items-center gap-1.5">
                    {h.kind === "auto" ? <Zap className="h-3 w-3" /> : <Hand className="h-3 w-3" />}
                    <span>{h.kind === "auto" ? "Auto" : "Manual"}</span>
                    <span>·</span>
                    <span>{new Date(h.sent_at).toLocaleString()}</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <StatusBadge
                  status={h.status}
                  label={h.status === "sent" ? "Sent" : h.status === "skipped" ? "Skipped" : h.status === "failed" ? "Failed" : h.status}
                />
                {h.error && <span className="text-xs text-slate-500 max-w-sm truncate" title={h.error}>{h.error}</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="kcis-overline mt-6 mb-3">Client portal access (last 10)</div>
      {accessLoading ? (
        <div className="text-sm text-slate-500 py-3"><Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading...</div>
      ) : accessLogs.length === 0 ? (
        <div className="text-sm text-slate-500 italic py-3" data-testid={`access-logs-empty-${assessment.client_id}`}>No portal sign-ins yet.</div>
      ) : (
        <div className="space-y-2" data-testid={`access-logs-${assessment.client_id}`}>
          {accessLogs.map((row) => (
            <div key={row.id} className="bg-white rounded-xl border border-slate-200 p-3 flex flex-wrap items-center justify-between gap-3 text-sm">
              <div className="flex items-center gap-3 min-w-0">
                <KeyRound className="h-4 w-4 text-purple-900 shrink-0" />
                <div className="min-w-0">
                  <div className="text-slate-900 font-medium truncate">
                    {row.access_type === "passcode_verified" ? "Signed in via passcode" : row.access_type}
                  </div>
                  <div className="text-xs text-slate-500 truncate">
                    {new Date(row.timestamp).toLocaleString()}
                    {row.ip ? ` · IP ${row.ip}` : ""}
                  </div>
                </div>
              </div>
              {row.user_agent && (
                <span className="text-[10px] text-slate-500 max-w-xs truncate" title={row.user_agent}>
                  {row.user_agent}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default function AdminDashboard({ mode = "admin" }) {
  const navigate = useNavigate();
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [proByClient, setProByClient] = useState({});
  const [reassignTarget, setReassignTarget] = useState(null); // super-admin admin reassign
  const [releaseTarget, setReleaseTarget] = useState(null);   // practitioner self-release
  const [transferTarget, setTransferTarget] = useState(null); // practitioner self-transfer
  const [transfersPanelOpen, setTransfersPanelOpen] = useState(false);
  const [pendingTransfers, setPendingTransfers] = useState([]);
  const isSuper = adminAuth.isSuperAdmin();
  const isPro = adminAuth.isPro();

  const loadPendingTransfers = async () => {
    if (isSuper) return;
    try {
      const { data } = await api.get("/practitioner/transfers/incoming");
      setPendingTransfers(data?.items || []);
    } catch { /* non-fatal */ }
  };

  const refreshAssessments = async () => {
    try { const { data } = await api.get("/assessments"); setAssessments(data); } catch { /* ignore */ }
  };

  useEffect(() => {
    if (!adminAuth.getToken()) { navigate("/admin/login"); return; }
    (async () => {
      try {
        const { data } = await api.get("/assessments");
        setAssessments(data);
        if (isPro) {
          try {
            const { data: pros } = await api.get("/pro/assessments");
            const grouped = {};
            for (const p of pros) {
              grouped[p.client_id] = (grouped[p.client_id] || 0) + 1;
            }
            setProByClient(grouped);
          } catch { /* non-fatal */ }
        }
      } catch (err) {
        if (err?.response?.status === 401 || err?.response?.status === 403) {
          // Never clear the session for a subscription_required 403 — the
          // global axios interceptor will redirect to /subscribe and the
          // token must still be present for /api/me/billing to authenticate.
          const detail = err?.response?.data?.detail;
          const isSubGate = detail && typeof detail === "object" && detail.code === "subscription_required";
          if (isSubGate) {
            return;  // interceptor handles the redirect
          }
          adminAuth.clear();
          navigate("/admin/login");
        } else {
          toast.error("Could not load assessments. Please try again.");
        }
      } finally {
        setLoading(false);
      }
    })();
    // Load pending transfers for non-super practitioners
    loadPendingTransfers();
    // If user arrived via email link `?transfer=<id>`, open the panel
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      if (params.get("transfer")) setTransfersPanelOpen(true);
    }
  }, [navigate, isPro]);

  const filtered = assessments.filter((a) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (a.name || "").toLowerCase().includes(s) ||
      (a.email || "").toLowerCase().includes(s) ||
      (a.primary_concern || "").toLowerCase().includes(s);
  });

  const uniqueClients = new Set(assessments.map((a) => a.client_id)).size;
  const stats = {
    clients: uniqueClients,
    assessments: assessments.length,
    reports: assessments.filter((a) => a.report_status === "generated").length,
    followups: assessments.filter((a) => a.followup_status === "submitted").length,
  };

  return (
    <div className="min-h-screen flex flex-col">
      <TopNav
        right={
          <div className="flex items-center gap-1">
            {!isSuper && (
              <Button
                variant="ghost"
                onClick={() => setTransfersPanelOpen(true)}
                className="rounded-full text-slate-700 relative"
                data-testid="pending-transfers-bell"
              >
                <Bell className="h-4 w-4 mr-2" />
                Transfers
                {pendingTransfers.length > 0 && (
                  <span
                    className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] rounded-full bg-red-600 text-white text-[10px] font-bold inline-flex items-center justify-center px-1"
                    data-testid="pending-transfers-badge"
                  >
                    {pendingTransfers.length}
                  </span>
                )}
              </Button>
            )}
            {isSuper && (
              <Link to="/admin/practitioners">
                <Button variant="ghost" className="rounded-full text-slate-700" data-testid="admin-manage-practitioners-link">
                  <Users className="h-4 w-4 mr-2" /> Clinic Staff / Care Team
                </Button>
              </Link>
            )}
            {isSuper && (
              <Link to="/admin/wellness-gateway-leads">
                <Button variant="ghost" className="rounded-full text-slate-700" data-testid="admin-wellness-gateway-link">
                  <Leaf className="h-4 w-4 mr-2" /> Wellness Gateway
                </Button>
              </Link>
            )}
            {isSuper && (
              <Link to="/admin/funnel-inquiries">
                <Button variant="ghost" className="rounded-full text-slate-700" data-testid="admin-funnel-inquiries-link">
                  <Inbox className="h-4 w-4 mr-2" /> Funnel Inquiries
                </Button>
              </Link>
            )}
            <Button
              variant="ghost"
              className="rounded-full text-slate-700"
              onClick={() => { adminAuth.clear(); navigate("/"); }}
              data-testid="admin-logout-btn"
            >
              <LogOut className="h-4 w-4 mr-2" /> Sign out
            </Button>
          </div>
        }
      />
      <main className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 w-full">
        <div className="kcis-fade-up">
          {/* Phase 6A — KCIS™ brand mark with optional Edition tagline below.
              Edition string comes from admins.subscription_tier via adminAuth.getEdition().
              For super_admin we show "Owner Workspace" instead of an edition label. */}
          <div className="kcis-overline text-purple-900" data-testid="kcis-brand-mark">
            {PRODUCT_NAME}
          </div>
          <div className="text-xs tracking-[0.18em] uppercase text-slate-500 font-medium mt-1" data-testid="kcis-edition-tagline">
            {isSuper ? "Owner Workspace" : (editionFor(adminAuth.getEdition()) || "")}
          </div>
          <h1 className="font-display text-3xl sm:text-4xl tracking-tight text-slate-900 mt-3">
            {mode === "practitioner" ? "Your clients" : "Client overview"}
          </h1>
          {!isSuper && (
            <p className="text-sm text-slate-500 mt-2">
              You're signed in as <span className="text-purple-900 font-medium">{adminAuth.getRole().replace("_", " ")}</span> · viewing only clients assigned to you.
            </p>
          )}
          <PractitionerActions />
        </div>

        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          <Stat icon={Users} label="Clients" value={stats.clients} />
          <Stat icon={FileText} label="Assessments" value={stats.assessments} />
          <Stat icon={FileText} label="Reports generated" value={stats.reports} />
          <Stat icon={ClipboardCheck} label="Follow-ups submitted" value={stats.followups} />
        </div>

        <div className="mt-10 kcis-card p-2">
          <div className="px-4 py-4 flex items-center gap-3 border-b border-slate-100">
            <Search className="h-4 w-4 text-slate-400" />
            <Input
              data-testid="admin-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, email, concern..."
              className="border-0 focus-visible:ring-0 bg-transparent h-10 px-0 shadow-none"
            />
          </div>
          {loading ? (
            <div className="p-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-purple-900" /></div>
          ) : (
            <Table data-testid="admin-clients-table">
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Client / Assessment</TableHead>
                  <TableHead>Concern</TableHead>
                  <TableHead>Report</TableHead>
                  <TableHead>Auto-send</TableHead>
                  <TableHead>Follow-up</TableHead>
                  <TableHead>Submitted</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.length === 0 && (
                  <TableRow><TableCell colSpan={7} className="text-center text-slate-500 py-12">No assessments yet.</TableCell></TableRow>
                )}
                {filtered.map((a) => {
                  const isOpen = expandedId === a.id;
                  return (
                    <Fragment key={a.id}>
                      <TableRow
                        className="hover:bg-slate-50/60 cursor-pointer"
                        onClick={() => setExpandedId(isOpen ? null : a.id)}
                        data-testid={`admin-row-${a.id}`}
                      >
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {isOpen ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
                            <div>
                              <div className="font-medium text-slate-900 flex items-center gap-2">
                                {a.name}
                                {proByClient[a.client_id] > 0 && (
                                  <span
                                    className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-purple-900 text-white"
                                    data-testid={`pro-badge-${a.client_id}`}
                                  >
                                    Pro · {proByClient[a.client_id]}
                                  </span>
                                )}
                              </div>
                              <div className="text-xs text-slate-500">{a.email} · age {a.age}</div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm text-slate-700 max-w-xs truncate">{a.primary_concern}</TableCell>
                        <TableCell><StatusBadge status={a.report_status} label={a.report_status === "generated" ? "Generated" : a.report_status === "error" ? "Error" : "Pending"} /></TableCell>
                        <TableCell><StatusBadge
                          status={a.auto_send_status || "not_attempted"}
                          label={
                            a.auto_send_status === "sent" ? "Sent" :
                            a.auto_send_status === "partial" ? "Partial" :
                            a.auto_send_status === "failed" ? "Failed" :
                            a.auto_send_status === "skipped" ? "Skipped" : "—"
                          }
                        /></TableCell>
                        <TableCell><StatusBadge status={a.followup_status} label={a.followup_status === "submitted" ? "Submitted" : "Not submitted"} /></TableCell>
                        <TableCell className="text-xs text-slate-500">{new Date(a.created_at).toLocaleDateString()}</TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`admin-row-menu-${a.id}`}>
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem asChild>
                                <Link to={`/report/${a.id}`}>View report</Link>
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => setExpandedId(a.id)}>
                                Send / Download
                              </DropdownMenuItem>
                              {isPro && (
                                <DropdownMenuItem asChild data-testid={`admin-row-pro-${a.id}`}>
                                  <Link to={`/admin/pro/${a.client_id}`}>
                                    Open KCIS Pro
                                  </Link>
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem asChild>
                                <Link to={`/client/followup/${a.id}`}>Submit follow-up</Link>
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => { navigator.clipboard.writeText(a.email); toast.success("Email copied"); }}>
                                Copy email
                              </DropdownMenuItem>
                              {!isSuper && (
                                <>
                                  <DropdownMenuItem
                                    onClick={() => setTransferTarget({
                                      client_id: a.client_id,
                                      client_name: a.name,
                                      client_email: a.email,
                                    })}
                                    data-testid={`admin-row-transfer-${a.id}`}
                                  >
                                    <UserPlus className="h-4 w-4 mr-2" />Transfer to another practitioner…
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() => setReleaseTarget({
                                      client_id: a.client_id,
                                      client_name: a.name,
                                      client_email: a.email,
                                    })}
                                    className="text-red-600 focus:text-red-700"
                                    data-testid={`admin-row-release-${a.id}`}
                                  >
                                    <UserX className="h-4 w-4 mr-2" />Release client
                                  </DropdownMenuItem>
                                </>
                              )}
                              {isSuper && (
                                <DropdownMenuItem
                                  onClick={() => setReassignTarget({
                                    client_id: a.client_id,
                                    client_name: a.name,
                                    client_email: a.email,
                                    current_practitioner_email: a.practitioner_email || "",
                                  })}
                                  data-testid={`admin-row-reassign-${a.id}`}
                                >
                                  Reassign practitioner…
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                      {isOpen && (
                        <TableRow key={`${a.id}-detail`} className="hover:bg-transparent">
                          <TableCell colSpan={7} className="p-0">
                            <AssessmentDrawer assessment={a} />
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </div>
      </main>
      {reassignTarget && (
        <ReassignPractitionerModal
          target={reassignTarget}
          onClose={() => setReassignTarget(null)}
          onSuccess={async () => {
            setReassignTarget(null);
            try {
              const { data } = await api.get("/assessments");
              setAssessments(data);
            } catch { /* ignore */ }
          }}
        />
      )}
      {releaseTarget && (
        <ReleaseClientModal
          target={releaseTarget}
          onClose={() => setReleaseTarget(null)}
          onSuccess={() => { setReleaseTarget(null); refreshAssessments(); }}
        />
      )}
      {transferTarget && (
        <TransferClientModal
          target={transferTarget}
          onClose={() => setTransferTarget(null)}
          onSuccess={() => { setTransferTarget(null); refreshAssessments(); }}
        />
      )}
      {transfersPanelOpen && (
        <PendingTransfersPanel
          items={pendingTransfers}
          onClose={() => setTransfersPanelOpen(false)}
          onResolved={async () => {
            await loadPendingTransfers();
            await refreshAssessments();
          }}
        />
      )}
      <Footer />
    </div>
  );
}

// ─────────────────────────── Reassign Practitioner Modal ───────────────
// Super-admin-only. Wires to PATCH /api/admin/clients/{client_id}/practitioner.
// The picker sources from GET /api/practitioners (db.practitioners) — that
// is the collection the PATCH endpoint validates the target against.
function ReassignPractitionerModal({ target, onClose, onSuccess }) {
  const [practitioners, setPractitioners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/practitioners");
        setPractitioners(data || []);
      } catch (e) {
        toast.error("Could not load practitioners");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const submit = async () => {
    if (!selectedId) { toast.error("Choose a target practitioner"); return; }
    setSubmitting(true);
    try {
      await api.patch(`/admin/clients/${target.client_id}/practitioner`, {
        practitioner_id: selectedId,
      });
      const chosen = practitioners.find((p) => p.id === selectedId);
      toast.success(`Reassigned ${target.client_name} to ${chosen?.email || "new practitioner"}`);
      onSuccess?.();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (detail?.message || "Reassignment failed"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="reassign-modal">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="inline-flex items-center gap-2 text-purple-900 mb-1">
              <ArrowRightLeft className="w-4 h-4" />
              <h3 className="font-display text-lg">Reassign practitioner</h3>
            </div>
            <p className="text-xs text-slate-500">Cascades to all assessments and Pro assessments for this client.</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="reassign-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 mb-4 text-xs space-y-1">
          <div><span className="text-slate-500">Client:</span> <span className="font-medium text-slate-900">{target.client_name}</span></div>
          <div><span className="text-slate-500">Email:</span> <span className="text-slate-800">{target.client_email}</span></div>
          <div><span className="text-slate-500">Current practitioner:</span> <span className="text-slate-800">{target.current_practitioner_email || "—"}</span></div>
        </div>

        <label className="block text-xs text-slate-600 mb-1.5">Reassign to:</label>
        {loading ? (
          <div className="h-10 flex items-center text-slate-500 text-sm"><Loader2 className="w-4 h-4 animate-spin mr-2" />Loading practitioners…</div>
        ) : (
          <select
            value={selectedId}
            onChange={(e) => setSelectedId(e.target.value)}
            className="w-full h-11 rounded-xl border border-slate-200 px-3 text-sm bg-white focus:outline-none focus:border-purple-900"
            data-testid="reassign-select"
          >
            <option value="">— Choose a practitioner —</option>
            {practitioners.map((p) => (
              <option key={p.id} value={p.id}>
                {p.email}{p.name ? ` (${p.name})` : ""}
              </option>
            ))}
          </select>
        )}

        <div className="mt-6 flex gap-2 justify-end">
          <button
            onClick={onClose}
            disabled={submitting}
            className="h-10 px-4 rounded-xl text-sm text-slate-600 hover:text-slate-900"
            data-testid="reassign-cancel"
          >Cancel</button>
          <button
            onClick={submit}
            disabled={submitting || !selectedId}
            className="h-10 px-5 rounded-xl bg-purple-900 hover:bg-purple-800 text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
            data-testid="reassign-confirm"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRightLeft className="w-4 h-4" />}
            Reassign
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────── Release Client Modal ──────────────────────
function ReleaseClientModal({ target, onClose, onSuccess }) {
  const [submitting, setSubmitting] = useState(false);
  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post(`/practitioner/clients/${target.client_id}/release`);
      toast.success(`${target.client_name} released from your roster`);
      onSuccess?.();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (detail?.message || "Release failed"));
    } finally { setSubmitting(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="release-modal">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="inline-flex items-center gap-2 text-red-700">
            <UserX className="w-5 h-5" />
            <h3 className="font-display text-lg">Release client</h3>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="release-close"><X className="w-4 h-4" /></button>
        </div>
        <p className="text-sm text-slate-700 leading-relaxed">
          Releasing <b>{target.client_name}</b> will remove them from your roster.
          Their history will be preserved but you will no longer be able to add new
          sessions or protocols. This cannot be undone.
        </p>
        <div className="mt-6 flex gap-2 justify-end">
          <button onClick={onClose} disabled={submitting} className="h-10 px-4 rounded-xl text-sm text-slate-600 hover:text-slate-900" data-testid="release-cancel">Cancel</button>
          <button
            onClick={submit} disabled={submitting}
            className="h-10 px-5 rounded-xl bg-red-600 hover:bg-red-700 text-white text-sm font-medium disabled:opacity-50 inline-flex items-center gap-2"
            data-testid="release-confirm"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserX className="w-4 h-4" />}
            Release Client
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────── Transfer Client Modal ─────────────────────
function TransferClientModal({ target, onClose, onSuccess }) {
  const [recipient, setRecipient] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!recipient.trim()) { toast.error("Enter the recipient practitioner's email"); return; }
    setSubmitting(true);
    try {
      const r = await api.post(`/practitioner/clients/${target.client_id}/transfer`, {
        recipient_email: recipient.trim().toLowerCase(),
        message: message.trim() || null,
      });
      toast.success(r.data?.message || "Transfer request sent");
      onSuccess?.();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (detail?.message || "Could not send transfer"));
    } finally { setSubmitting(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="transfer-modal">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="inline-flex items-center gap-2 text-purple-900">
            <UserPlus className="w-5 h-5" />
            <h3 className="font-display text-lg">Transfer client</h3>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="transfer-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 mb-4 text-xs space-y-1">
          <div><span className="text-slate-500">Client:</span> <span className="font-medium text-slate-900">{target.client_name}</span></div>
          <div><span className="text-slate-500">Email:</span> <span className="text-slate-800">{target.client_email}</span></div>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <label className="block">
            <span className="text-xs text-slate-600 mb-1.5 inline-flex items-center gap-1.5">
              <Mail className="w-3.5 h-3.5 text-slate-400" />
              Recipient practitioner email *
            </span>
            <input
              required type="email" value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
              placeholder="practitioner@example.com"
              className="w-full h-11 rounded-xl border border-slate-200 px-3 text-sm bg-white focus:outline-none focus:border-purple-900"
              data-testid="transfer-recipient-input"
            />
          </label>
          <label className="block">
            <span className="text-xs text-slate-600 mb-1.5 inline-flex items-center gap-1.5">
              <MessageSquare className="w-3.5 h-3.5 text-slate-400" />
              Optional message ({200 - message.length} chars left)
            </span>
            <textarea
              rows={3} value={message} maxLength={200}
              onChange={(e) => setMessage(e.target.value)}
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm bg-white focus:outline-none focus:border-purple-900 resize-none"
              data-testid="transfer-message-input"
            />
          </label>
          <div className="pt-1 flex gap-2 justify-end">
            <button type="button" onClick={onClose} disabled={submitting} className="h-10 px-4 rounded-xl text-sm text-slate-600 hover:text-slate-900" data-testid="transfer-cancel">Cancel</button>
            <button
              type="submit" disabled={submitting || !recipient.trim()}
              className="h-10 px-5 rounded-xl bg-purple-900 hover:bg-purple-800 text-white text-sm font-medium disabled:opacity-50 inline-flex items-center gap-2"
              data-testid="transfer-confirm"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Send Transfer Request
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─────────────────────────── Pending Transfers Panel ───────────────────
function PendingTransfersPanel({ items, onClose, onResolved }) {
  const [working, setWorking] = useState({}); // id -> 'accept' | 'decline'
  const act = async (id, action) => {
    setWorking((w) => ({ ...w, [id]: action }));
    try {
      await api.post(`/practitioner/transfers/${id}/${action}`);
      toast.success(action === "accept" ? "Client added to your roster" : "Transfer declined");
      onResolved?.();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (detail?.message || "Action failed"));
    } finally {
      setWorking((w) => { const c = { ...w }; delete c[id]; return c; });
    }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 pt-16" data-testid="transfers-panel">
      <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full p-6 max-h-[80vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-4">
          <div className="inline-flex items-center gap-2 text-purple-900">
            <Bell className="w-5 h-5" />
            <h3 className="font-display text-lg">Pending client transfers</h3>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="transfers-panel-close"><X className="w-4 h-4" /></button>
        </div>
        {items.length === 0 ? (
          <div className="rounded-xl bg-slate-50 border border-slate-200 p-8 text-center text-sm text-slate-500" data-testid="transfers-panel-empty">
            No pending transfers.
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((t) => (
              <div key={t.id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`transfer-row-${t.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-slate-900">{t.client_name || t.client_email}</div>
                    <div className="text-xs text-slate-500">{t.client_email}</div>
                    <div className="text-xs text-slate-600 mt-1.5">
                      From: <span className="text-slate-900">{t.from_practitioner_name || t.from_practitioner_email}</span>
                    </div>
                    {t.message && (
                      <div className="mt-2 border-l-2 border-purple-900 pl-3 py-1 text-xs text-slate-700 bg-purple-50/50 rounded-r">
                        {t.message}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button
                      onClick={() => act(t.id, "decline")}
                      disabled={!!working[t.id]}
                      className="h-9 px-3 rounded-xl text-xs text-slate-600 border border-slate-200 hover:bg-slate-50 disabled:opacity-50"
                      data-testid={`transfer-decline-${t.id}`}
                    >
                      {working[t.id] === "decline" ? <Loader2 className="w-3 h-3 animate-spin" /> : "Decline"}
                    </button>
                    <button
                      onClick={() => act(t.id, "accept")}
                      disabled={!!working[t.id]}
                      className="h-9 px-4 rounded-xl text-xs bg-purple-900 hover:bg-purple-800 text-white font-medium disabled:opacity-50 inline-flex items-center gap-1.5"
                      data-testid={`transfer-accept-${t.id}`}
                    >
                      {working[t.id] === "accept" ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                      Accept
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

